import { toast } from "$global-providers/toast-provider";
import { gql } from "@apollo/client";
import type { PriceTariff } from "elephant-web/gql";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useFetchTariffsAndInsuranceProvidersQuery } from "./__generated__/use-tariffs-query.gql";

gql`
  query fetchTariffsAndInsuranceProviders {
    minInsuranceProviders {
      id
      name
    }
    priceListV2 {
      priceTariffs {
        edges {
          id
          name
          externalReference {
            foreignId
            origin
          }
        }
      }
    }
  }
`;

interface Insurer {
  id: string;
  name: string;
}

export const formatInsurersName = (
  insurers: Insurer[],
  priceTariffs: PriceTariff[],
): Insurer[] =>
  priceTariffs?.map((pt) => {
    const insurer = insurers?.find(
      (i) => pt?.externalReference?.foreignId === i.id,
    );
    return {
      id: pt.id,
      name: insurer?.name ? insurer.name : pt.name,
    };
  });

const usePriceTariffsQuery = () => {
  const { formatMessage } = useIntl();
  const { data, loading, error } = useFetchTariffsAndInsuranceProvidersQuery();

  useEffect(() => {
    if (error) {
      toast.error(
        formatMessage(
          {
            id: "useTariffsQuery.fetchError",
            defaultMessage: "Failed to fetch price types: {error}",
          },
          { error: error.message },
        ),
      );
    }
  }, [error, formatMessage]);

  const tariffs = formatInsurersName(
    // @ts-ignore: possible null value
    data?.minInsuranceProviders,
    data?.priceListV2?.priceTariffs?.edges,
  );
  return { tariffs, loading };
};

export default usePriceTariffsQuery;
